// ----------------------------------------------------------------------
// <copyright file="Izenda.TenantManagementStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, observable, toJS } from 'mobx';
import fetcher from 'fetchers/Izenda.TenantManagementDataFetcher';
import accessFetcher from 'fetchers/Izenda.AccessDataFetcher';
import TenantDetails from 'logic/models/Izenda.TenantDetails';
import TenantList from 'logic/models/Izenda.TenantList';
import constants from 'logic/data/Izenda.TenantConstants';
import CommonMessages from 'share/messages/Izenda.CommonMessages';
import PermissionStore from 'logic/Izenda.PermissionStore';
import AccessStore from 'logic/Izenda.PermissionAccessStore';
import TenantGroupingList from 'logic/models/Izenda.TenantGroupingList';
import localizer from 'share/localization/Izenda.Localizer';
import uiState from 'logic/Izenda.UiState';
import * as _ from 'lodash/fp';
import { EMPTY_GUID } from './data/Izenda.AppGlobalConstants';
import AuthorizationConstants from 'logic/data/Izenda.AuthorizationConstants';
import SecurityManager from 'share/authorization/Izenda.SecurityManager';

const IS_TENANT_SETUP = true;

export default class TenantManagementStore {
  @observable showingTenantCloned = {};
  @observable showingTenant = new TenantDetails();
  @observable tenantList = new TenantList();
  @observable modulesOfLicense = [];
  @observable alertMessage = '';

  /**
   * Available tenant groupings
   * @type {TenantGroupingList}
   */
  @observable groupingList = new TenantGroupingList();

  noDirtyFnRegistration: false; // if true - we don't register dirty function in uiStore.
  dirtyFn = () => _.get('showingTenantCloned.hasDirty', this); // Dirty check function

  constructor(noDirtyFnRegistration: boolean) {
    this.noDirtyFnRegistration = !!noDirtyFnRegistration;
    this.accessStore = new AccessStore();
  }

  /**
   * inititial for role setup store
   * @method init
   */
  init() {
    // whenever uiState changed the tenant.
    if (!this.noDirtyFnRegistration)
      uiState.setCheckDirtyFunction(this.dirtyFn);
  }

  /**
   * destroy observe
   * @method destroy
   */
  destroy() {
    if (!this.noDirtyFnRegistration)
      uiState.resetCheckDirtyFunction();
  }

  /**
   * Load tenants and it's groups structure.
   * @param {string[]} selectedItems Selected item ids.
   * @returns {Promise}
   */
  static async getTenantGroupsHierarchy(selectedItems) {
    const user = uiState.currentUser;
    const permissionKeys = AuthorizationConstants.PERMISSION_KEY;
    const securityManager = new SecurityManager();
    const hasAtLeastOnePermissionOfTenantSetup = securityManager.checkPrivileges([permissionKeys.TenantSetupActionsCreate,
      permissionKeys.TenantSetupActionsEdit, permissionKeys.TenantSetupActionsDel], AuthorizationConstants.PERMISSION_OPERATOR.Or);
    const hasPermissionsToRequestTenants = user.systemAdmin || (user.isSystemUser && hasAtLeastOnePermissionOfTenantSetup)

    const data = hasPermissionsToRequestTenants ? await fetcher.getTenantGroupsHierarchy() : null;
    if (!data || !_.isArray(data)) {
      return [];
    }
    // Data format is array of items:
    // ["tenantName", [{tenantId:'xxx', tenantGroupId:'yyy', tenantName: 'xxx_name', tenantGroupName: 'yyy_name'},...]]
    return _.flow(
      _.sortBy(['tenantGroupName']),
      _.groupBy('tenantGroupName'),
      _.toPairs,
      _.map(x => {
        const tenantGroupId = _.getOr(EMPTY_GUID, '[1][0].tenantGroupId', x);
        const isChecked = tenantGroupId !== EMPTY_GUID && !!_.find(x => x === tenantGroupId, selectedItems || []);
        return {
          checked: isChecked,
          disabled: isChecked,
          tenantGroupId: tenantGroupId,
          tenantGroupName: x[0] || localizer.getText('COMMON_LABEL_UNCATEGORIZED'),
          tenantGroupItems: _.map(item => {
            const tenantId = _.getOr(EMPTY_GUID, 'tenantId', item);
            const isItemChecked = tenantId !== EMPTY_GUID
              && (isChecked || !!_.find(x => x === tenantId, selectedItems));
            return _.defaults(item, { checked: isItemChecked, disabled: isItemChecked });
          }, x[1] || [])
        }
      })
    )(data);
  }

  /**
   * Get Initial Data
   * @param {boolean} isSelectFirstTenant Load first tenant data and set up as "showingTenant" if true
   * @param {boolean} doNotAddNewTenant Do not add new empty tenant if tenant list is empty
   */
  @action
  getTenantList(isSelectFirstTenant, doNotAddNewTenant) {
    this.resetAlertMessage();
    return fetcher.getAllBasicTenantList()
    .then(result => {
      const tenantsData = result || [];
      // populate tenant list
      this.tenantList.populateData(tenantsData);
      // set up default tenant
      if (tenantsData.length > 0) {
        if (!_.isBoolean(isSelectFirstTenant) && isSelectFirstTenant) {
          const tenant = this.tenantList.getFilterList()[0];
          this
          .loadTenant(tenant)
          .then(() => tenantsData);
          return null;
        }
      } else {
        if (!doNotAddNewTenant)
          this.addTenant();
      }
      return tenantsData;
    });
  }

  /**
   * Load groupings from API
   */
  @action
  loadGroupingsList() {
    return fetcher
    .getAllTenantGroups()
    .then(result => {
      this.groupingList.clear();
      if (_.isArray(result))
        this.groupingList.populateData(result);
      return this.groupingList;
    });
  }

  /**
   * Get modules of License
   */
  getModulesOfLicense() {
    return fetcher
    .getAllModulesOfLicense()
    .then(result => {
      this.modulesOfLicense = result || [];
      return result;
    });
  }

  /**
   * Set showing tenant
   */
  @action
  setShowingTenant(tenant) {
    // reset data of previous tenant
    this.showingTenant && this.showingTenant.setSelected(false);

    // update new selected tenant
    tenant && (this.showingTenant = tenant);
    this.showingTenant.setSelected(true);
    this.loadGroupingsList().then((a) => {});
  }

  /**
   * set showing tenant by index
   * @param {number} index
   */
  setShowingTenantByIndex(index) {
    const list = this.tenantList.tenantList;
    this.loadTenant(list[index]);
  }

  /**
   * Add tenant
   */
  @action
  addTenant() {
    this.showingTenant.setSelected(false);
    this.showingTenantCloned.setSelected(false);

    // Extract all modules's name and add it to a new Tenant
    const tenantModules = _.map('name', this.modulesOfLicense);

    this.showingTenantCloned = new TenantDetails({tenantModules: tenantModules});

    this.permissionStore = new PermissionStore(this.showingTenantCloned.permission, null, IS_TENANT_SETUP);
    this.accessStore.updateModel(this.showingTenantCloned.permissionAccessModel);
    this.accessStore.initialAccessGrid();

    this.resetAlertMessage();
  }

  /**
   * Update Filter Text
   * @param newValue
   */
  updateFilterText(newValue) {
    this.tenantList.filterText = newValue;
    if (this.tenantList.filterTenants.length > 0) {
      this.setShowingTenant(this.tenantList.filterTenants[0]);
    }
  };

  /**
   * Save tenant
   * @returns {*|Promise.<T>}
   */
  saveTenant() {
    const savedTenant = this.showingTenantCloned;
    //Get update access
    const updatedAccess = this.showingTenantCloned.permissionAccessModel.getAccessForSave();
    //Update list access default
    savedTenant.permission.updateAccessDefault(updatedAccess);
    // Update tenant group for saving
    const savedTenantJson = savedTenant.toJson();

    return fetcher
    .saveTenant(savedTenantJson)
    .then(response => {
      if (response.success) {
        if (savedTenant.isNew) {
          this.getTenantList(false).then(() => {
            const tenantDetail = _.find({id: response.tenant.id}, this.tenantList.tenantList);
            tenantDetail && this.loadTenant(tenantDetail);
          });
        } else {
          this.showingTenant.populateData(response.tenant);
          this.loadTenant(this.showingTenant);
        }
        this.showingTenantCloned.cleanDirty();
        this.showingTenant.updateVersion();
        this.accessStore.updateAccessOrigin(this.showingTenantCloned.permission.access.accessDefaults.value);
      }
      return response;
    })
    .then(response => {
      this.alertMessage = {
        type: response.success ? 'success' : 'error',
        message: response.success
          ? localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
          : response.messages[0].messages || null
      };
      return response.messages;
    })
    .then(response => {
      // Reload tenant groups (because list of groups can be changed after saving tenant)
      return new Promise(resolve => {
        this
        .loadGroupingsList()
        // pass through current response
        .then(() => resolve(response));
      });
    });
  };

  /**
   * Delete tenant
   * @param id
   * @returns {*|Promise.<T>}
   */
  @action
  deleteTenant(id) {
    let showingTenantIndex = _.findIndex({id: id}, this.tenantList.tenantList);

    const setTenant = action(() => {
      const list = this.tenantList.tenantList;
      if (list.length > 0) {
        if (showingTenantIndex >= list.length) {
          showingTenantIndex = list.length - 1;
        }
        this.setShowingTenantByIndex(showingTenantIndex);
      }
    });

    return fetcher.deleteTenant(id).then(response => {
      if (response.success) {
        this.getTenantList().then(setTenant);
        return Promise.resolve(true);
      }
      return response;
    });
  }

  /**
   * Active Tenant
   * @param tenant
   * @returns {Promise.<T>}
   */
  activeTenant(tenant) {
    tenant.setActive(true);
    this.showingTenant === tenant && this.setTenantStatus(tenant, true);
    return fetcher.activeTenant(tenant.id);
  }

  /**
   * set status of the virtunal tenant detail
   * @param  {object} tenant
   * @param  {boolean} state)
   */
  @action
  setTenantStatus(tenant, state) {
    tenant && (this.showingTenant = tenant);
    this.showingTenant.setActive(state);
    this.cloneTenantDetail();
  }

  /**
   * Deactive Tenant
   * @param tenant
   * @returns {Promise.<T>}
   */
  @action
  deactiveTenant(tenant) {
    tenant.setActive(false);
    this.showingTenant === tenant && this.setTenantStatus(tenant, false);
    return fetcher.deactiveTenant(tenant.id);
  }

  /**
   * Get Bundle
   */
  getBundle() {
    return constants;
  }

  /**
   * Set Alert message
   */
  setAlertMessage(type, message) {
    this.alertMessage = {
      type: type,
      message: message
    };
  }

  /**
   * Reset Alert message
   */
  resetAlertMessage() {
    this.alertMessage = CommonMessages.EMPTY_MESSAGE;
  }

  /**
   * clone to new virtual TenantDetail
   * @return {Object} [this.showingTenantCloned]
   */
  cloneTenantDetail() {
    this.showingTenantCloned = new TenantDetails(_.cloneDeep(toJS(this.showingTenant)));
  }

  /**
   * Load tenant detail
   * @return {Object} Promise
   */
  @action
  loadTenant(tenant) {
    const tenantDetail = action(result => {
      tenant.populateData(result);
      this.setShowingTenant(tenant);
      this.cloneTenantDetail();
      this.permissionStore = new PermissionStore(this.showingTenantCloned.permission, tenant.id, IS_TENANT_SETUP);
      this.accessStore.updateModel(this.showingTenantCloned.permissionAccessModel);
      this.accessStore.initialAccessGrid(result.permission.access.accessDefaults.value);
      return Promise.resolve();
    });
    return fetcher.loadTenant(tenant.id).then(tenantDetail);
  }

  /**
   * Validation list accesses setting
   * @return {object} Promise result
   */
  validateAccesses() {
    const updatedAccess = this.showingTenantCloned.permissionAccessModel.getAccessForSave(true);
    return accessFetcher.validateAccesses(updatedAccess);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.TenantManagementStore.js